type KalturaPlayerConstructor = {
    setup: (config: {
        targetId: string;
        provider: {
            partnerId: number;
            uiConfId: number;
        };
        playback?: {
            textLanguage?: string;
            captionsDisplay?: boolean;
        };
        plugins?: Record<string, { disable?: boolean }>;
    }) => KalturaPlayerInstance;
};

export type KalturaPlayerInstance = {
    loadMedia: (config: { entryId: string | number }) => Promise<unknown> | unknown;
    play?: () => void;
    pause?: () => void;
    destroy?: () => void;
};

type CreateKalturaPlayerConfig = {
    targetId: string;
    partnerId: string;
    playerId: string;
    entryId: string | number;
    textLanguage?: string;
    disableTranscriptPlugin?: boolean;
};

const KALTURA_PLAYER_GLOBAL = 'KalturaPlayer';
const scriptPromises = new Map<string, Promise<KalturaPlayerConstructor>>();

function getScriptKey(partnerId: string, playerId: string): string {
    return `${partnerId}:${playerId}`;
}

export function loadKalturaPlayerScript(partnerId: string, playerId: string): Promise<KalturaPlayerConstructor> {
    const kalturaWindow = window as Window & { KalturaPlayer?: KalturaPlayerConstructor };

    if (kalturaWindow[KALTURA_PLAYER_GLOBAL]) {
        return Promise.resolve(kalturaWindow[KALTURA_PLAYER_GLOBAL]);
    }

    const existingPromise = scriptPromises.get(getScriptKey(partnerId, playerId));
    if (existingPromise) {
        return existingPromise;
    }

    const scriptPromise = new Promise<KalturaPlayerConstructor>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://api.frp2.ovp.kaltura.com/p/${partnerId}/embedPlaykitJs/uiconf_id/${playerId}`;
        script.async = true;

        script.onload = () => {
            if (kalturaWindow[KALTURA_PLAYER_GLOBAL]) {
                resolve(kalturaWindow[KALTURA_PLAYER_GLOBAL]);
                return;
            }
            scriptPromises.delete(getScriptKey(partnerId, playerId));
            reject(new Error('Kaltura player script loaded but KalturaPlayer is unavailable.'));
        };
        script.onerror = () => {
            scriptPromises.delete(getScriptKey(partnerId, playerId));
            reject(new Error('Failed to load the Kaltura player script.'));
        };

        document.head.appendChild(script);
    });

    scriptPromises.set(getScriptKey(partnerId, playerId), scriptPromise);
    return scriptPromise;
}

export async function createKalturaPlayer(config: CreateKalturaPlayerConfig): Promise<KalturaPlayerInstance> {
    const KalturaPlayer = await loadKalturaPlayerScript(config.partnerId, config.playerId);
    const playerInstance = KalturaPlayer.setup({
        targetId: config.targetId,
        provider: {
            partnerId: Number(config.partnerId),
            uiConfId: Number(config.playerId),
        },
        playback: {
            textLanguage: config.textLanguage,
            captionsDisplay: true,
        },
        plugins: config.disableTranscriptPlugin ? {
            transcript: {
                disable: true,
            },
            'playkit-transcript': {
                disable: true,
            },
        } : undefined,
    });

    await Promise.resolve(playerInstance.loadMedia({
        entryId: config.entryId,
    }));

    return playerInstance;
}

export function destroyKalturaPlayer(playerInstance: KalturaPlayerInstance | null): void {
    if (playerInstance && typeof playerInstance.destroy === 'function') {
        playerInstance.destroy();
    }
}
